import { GridDisplaySettings } from '@app/models';
import { DossierSearchActions, DossierSearchActionTypes, SearchDossiersSuccess } from '@app/pcs/actions/dossier-search';
import { SetUserGridConfiguration, UserGridConfigurationActionTypes } from '@app/pcs/actions/user-grid-configuration';
import { DossierSearchKey, DossierSearchType } from '@app/pcs/models';
import { UserGridType } from '@app/pcs/models/user-grid-type';
import { isDossierNavigationAction } from '@app/pcs/util';
import { BackButtonActionTypes } from '@dossier/actions/back-button';
import { Action } from '@ngrx/store';
import { createHandler, createReducer, isDefined, persistReducer } from '@util';
import * as R from 'ramda';

interface LocalStorageState {
  searchType: DossierSearchType;
}

export interface State extends LocalStorageState {
  dashboardSearchType: DossierSearchType;
  userGridType: UserGridType;
  configuration: GridDisplaySettings;
}

export const initialState: State = {
  searchType: null,
  dashboardSearchType: null,
  userGridType: null,
  configuration: null
};

type GetDossierSearchType = (s: State) => DossierSearchType;
export const getDossierSearchType: GetDossierSearchType = R.prop('searchType');

export const getDashboardSearchType: GetDossierSearchType = R.prop('dashboardSearchType');

type GetUserGridType = (s: State) => UserGridType;
export const getUserGridType: GetUserGridType = R.prop('userGridType');

type GetConfiguration = (s: State) => GridDisplaySettings;
export const getConfiguration: GetConfiguration = R.prop('configuration');

const DASHBOARD_SEARCH_TYPES = [
  DossierSearchType.LABELS,
  DossierSearchType.FILTER,
  DossierSearchType.QUICK_SEARCH,
  DossierSearchType.RECENT_ACTIVITY
];

type IsDashboardSearchType = (searchType: DossierSearchType) => boolean;
const isDashboardSearchType: IsDashboardSearchType = R.includes(R.__, DASHBOARD_SEARCH_TYPES);

const setSearchType = (state: State, searchType: DossierSearchType): State => {
  const dashboardSearchType = isDashboardSearchType(searchType) ? searchType : state.dashboardSearchType;

  return R.mergeRight(state, {searchType, dashboardSearchType});
};

const searchDossiersSuccess = (state: State, {payload: searchType}: SearchDossiersSuccess): State =>
  setSearchType(state, searchType);

const setUserGridType = (state: State, {payload: {userGridType, configuration}}: SetUserGridConfiguration): State =>
  R.mergeRight(state, {userGridType, configuration: isDefined(configuration) ? configuration : null});

const resetUserGridType = (state: State): State => R.mergeRight(state, {userGridType: null, configuration: null});

const searchDossiersSuccessHandler = createHandler(
  DossierSearchActionTypes.SearchDossiersSuccess,
  searchDossiersSuccess
);

const setUserGridTypeHandler = createHandler(UserGridConfigurationActionTypes.SetUserGridConfiguration, setUserGridType);
const resetUserGridTypeHandler = createHandler(UserGridConfigurationActionTypes.ResetUserGridConfiguration, resetUserGridType);

export const reducer = persistReducer<State, LocalStorageState>(
  {
    storageKey: DossierSearchKey.DossierSearch,
    shouldSync: (_, action: Action): boolean => isDossierNavigationAction(action),
    shouldReinitialize: (_, action: Action): boolean => action.type === BackButtonActionTypes.GoBack,
    mapper: ({searchType}: State): LocalStorageState => ({searchType}),
    mergeStates: (state: State, {searchType}: LocalStorageState): State => setSearchType(state, searchType),
  },
  createReducer<State, DossierSearchActions>([
    searchDossiersSuccessHandler,
    setUserGridTypeHandler,
    resetUserGridTypeHandler
  ], initialState)
);
